import { useState, useRef, useEffect } from "react";
import { Heart, MessageCircle, Share2, Bookmark, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { Slider } from "@/components/ui/slider";
import { processVideoForShare } from "@/utils/mediaUtils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { useInView } from "react-intersection-observer";
import { useAuth } from "@/contexts/AuthContext";
import { useDebounce } from "@/hooks/useDebounce";

interface QuixVideoProps {
  video: {
    id: string;
    url: string;
    description: string;
    profiles: {
      username: string;
      avatar_url: string;
    };
    likes_count: number;
    comments_count: number;
    shares_count: number;
    bookmarks_count: number;
  };
  autoPlay?: boolean;
}

export const QuixVideo = ({ video, autoPlay = true }: QuixVideoProps) => {
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [localLikes, setLocalLikes] = useState(video.likes_count);
  const [localBookmarks, setLocalBookmarks] = useState(video.bookmarks_count);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [lastTap, setLastTap] = useState(0);
  const [showLikeAnimation, setShowLikeAnimation] = useState(false);
  const [isScrubbingActive, setIsScrubbingActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.5
  });

  useEffect(() => {
    if (!user) return;
    
    const checkUserReactions = async () => {
      try {
        const { data: likeData, error: likeError } = await supabase
          .from('quix_reactions')
          .select('*')
          .eq('video_id', video.id)
          .eq('user_id', user.id)
          .eq('reaction_type', 'like')
          .maybeSingle();
        
        if (likeError) throw likeError;
        setIsLiked(!!likeData);
        
        const { data: bookmarkData, error: bookmarkError } = await supabase
          .from('quix_bookmarks')
          .select('*')
          .eq('video_id', video.id)
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (bookmarkError) throw bookmarkError;
        setIsBookmarked(!!bookmarkData);
        
      } catch (error) {
        console.error("Error checking user reactions:", error);
      }
    };
    
    checkUserReactions();
  }, [user, video.id]);

  useEffect(() => {
    if (videoRef.current) {
      if (inView) {
        videoRef.current.play().catch(() => {
          console.log("Autoplay prevented");
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, [inView]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, []);

  const handleSeek = (value: number[]) => {
    if (videoRef.current) {
      videoRef.current.currentTime = value[0];
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to like videos",
        variant: "destructive",
      });
      return;
    }
    
    try {
      if (isLiked) {
        await supabase
          .from('quix_reactions')
          .delete()
          .eq('video_id', video.id)
          .eq('user_id', user.id)
          .eq('reaction_type', 'like');
        setLocalLikes(prev => prev - 1);
      } else {
        await supabase
          .from('quix_reactions')
          .insert([{ 
            video_id: video.id, 
            user_id: user.id, 
            reaction_type: 'like' 
          }]);
        setLocalLikes(prev => prev + 1);
      }
      setIsLiked(!isLiked);
    } catch (error) {
      console.error("Error updating reaction:", error);
      toast({
        title: "Error",
        description: "Failed to update reaction",
        variant: "destructive",
      });
    }
  };

  const handleBookmark = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to bookmark videos",
        variant: "destructive",
      });
      return;
    }
    
    try {
      if (isBookmarked) {
        await supabase
          .from('quix_bookmarks')
          .delete()
          .eq('video_id', video.id)
          .eq('user_id', user.id);
        setLocalBookmarks(prev => prev - 1);
      } else {
        await supabase
          .from('quix_bookmarks')
          .insert([{ 
            video_id: video.id, 
            user_id: user.id 
          }]);
        setLocalBookmarks(prev => prev + 1);
      }
      setIsBookmarked(!isBookmarked);
    } catch (error) {
      console.error("Error updating bookmark:", error);
      toast({
        title: "Error",
        description: "Failed to update bookmark",
        variant: "destructive",
      });
    }
  };

  const handleShare = async () => {
    try {
      toast({
        title: "Processing video",
        description: "Please wait while we prepare your video for sharing...",
      });

      console.log("Starting video processing...");
      const processedVideo = await processVideoForShare(video.url, video.profiles.username);
      console.log("Video processing complete");
      
      const url = URL.createObjectURL(processedVideo);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fluxvoid-${video.profiles.username}-${Date.now()}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      await supabase
        .from('quix_videos')
        .update({ shares_count: video.shares_count + 1 })
        .eq('id', video.id);

      toast({
        title: "Success",
        description: "Video processed and downloaded successfully",
      });
    } catch (error) {
      console.error("Share error:", error);
      toast({
        title: "Error",
        description: "Failed to process and share video. Please try again.",
        variant: "destructive",
      });
    }
  };

  const fetchComments = async () => {
    const { data, error } = await supabase
      .from('quix_comments')
      .select(`
        *,
        profiles:user_id (
          username,
          avatar_url
        )
      `)
      .eq('video_id', video.id)
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to fetch comments",
        variant: "destructive",
      });
      return;
    }

    setComments(data || []);
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;

    try {
      await supabase
        .from('quix_comments')
        .insert([{ 
          video_id: video.id, 
          content: newComment.trim() 
        }]);

      setNewComment("");
      fetchComments();
      
      toast({
        title: "Success",
        description: "Comment added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add comment",
        variant: "destructive",
      });
    }
  };

  const handleDoubleTap = () => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300; // ms
    
    if (now - lastTap < DOUBLE_TAP_DELAY) {
      if (!isLiked) {
        handleLike();
        setShowLikeAnimation(true);
        setTimeout(() => setShowLikeAnimation(false), 1000);
      }
    }
    
    setLastTap(now);
  };

  return (
    <div className="relative h-full w-full snap-start" ref={inViewRef}>
      <div className="absolute inset-0" ref={videoContainerRef}>
        <video
          ref={videoRef}
          src={video.url}
          className="h-full w-full object-cover"
          playsInline
          loop
        />
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />

      <div 
        className="absolute inset-0 z-10"
        onClick={handleDoubleTap}
      >
        {showLikeAnimation && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Heart className="h-24 w-24 text-red-500 fill-red-500 animate-pulse" />
          </div>
        )}
      </div>

      <div className="absolute bottom-[20%] left-0 right-0 flex justify-between items-end p-4 z-20">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Avatar className="h-8 w-8 ring-2 ring-white/50">
              <AvatarImage src={video.profiles.avatar_url} />
              <AvatarFallback>{video.profiles.username[0].toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="font-medium text-white">@{video.profiles.username}</span>
          </div>
          <p className="text-sm text-white/90 line-clamp-2">{video.description}</p>
        </div>

        <div className="flex flex-col gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full bg-black/20 backdrop-blur-sm w-8 h-8"
            onClick={handleLike}
          >
            <Heart
              className={`h-4 w-4 ${
                isLiked ? "text-red-500 fill-red-500" : "text-white"
              }`}
            />
          </Button>

          <Sheet open={isCommentsOpen} onOpenChange={setIsCommentsOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full bg-black/20 backdrop-blur-sm w-8 h-8"
                onClick={fetchComments}
              >
                <MessageSquare className="h-4 w-4 text-white" />
              </Button>
            </SheetTrigger>
            <SheetContent 
              side="bottom" 
              className="h-[80vh] rounded-t-[10px] px-4"
            >
              <SheetHeader className="pb-4">
                <SheetTitle>Comments</SheetTitle>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto -mx-4 px-4">
                {comments.map((comment) => (
                  <div key={comment.id} className="flex items-start gap-2 py-3 border-b border-gray-200/10">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={comment.profiles.avatar_url} />
                      <AvatarFallback>{comment.profiles.username[0].toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">@{comment.profiles.username}</p>
                      <p className="text-sm text-gray-400">{comment.content}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur border-t">
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleComment()}
                    className="flex-1"
                  />
                  <Button onClick={handleComment}>Post</Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <Button
            variant="ghost"
            size="icon"
            className="rounded-full bg-black/20 backdrop-blur-sm w-8 h-8"
            onClick={handleShare}
          >
            <Share2 className="h-4 w-4 text-white" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="rounded-full bg-black/20 backdrop-blur-sm w-8 h-8"
            onClick={handleBookmark}
          >
            <Bookmark
              className={`h-4 w-4 ${
                isBookmarked ? "text-yellow-500 fill-yellow-500" : "text-white"
              }`}
            />
          </Button>
        </div>
      </div>

      <div 
        className="absolute bottom-6 left-0 right-0 px-4 pb-0 z-30"
        onMouseEnter={() => setIsScrubbingActive(true)}
        onMouseLeave={() => setIsScrubbingActive(false)}
        onTouchStart={() => setIsScrubbingActive(true)}
        onTouchEnd={() => setIsScrubbingActive(false)}
      >
        <div 
          className={`transition-all duration-200 ${isScrubbingActive ? 'opacity-100 scale-y-125 group' : 'opacity-70 scale-y-75'}`}
        >
          <Slider
            value={[currentTime]}
            min={0}
            max={duration || 100}
            step={0.1}
            onValueChange={handleSeek}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};
